/** @jsxImportSource ./simboluri-jsx */
// PAGINA ELEMENTULUI (mock-ui/design/StrElement.dc.html) — obiectul central
// al capitolului 5: identitate + poziția în clădire + TOATE verificările lui
// (indexul invers) + istoricul op-urilor Pasului 19. „De ce →" duce la pasul
// care a emis verificarea, cu elementul deja selectat (?focus=).

import { useMemo, useState } from "react";
import { useT } from "../i18n";
import { ArmareResult, FemResult, GrindaArmata, StepState } from "./tipuri";
import { useCatalogVerificari } from "./useCatalog";
import DesenElement, { Vedere3DElement, areVedere3D } from "./DesenElement";
import InventarElement from "./InventarElement";
import PuncteGrinda, {
  BaraEfortRezistenta,
  MiniPozitie,
  tonVerdict,
} from "./PuncteGrinda";
import LocalizareCladire from "./LocalizareCladire";
import { croireAxului } from "./StepArmareGrinzi";
import InfasuratoareAx from "./InfasuratoareAx";
import { AxArmat, CroireAxFundare } from "./armareAx";
import VerificareDetaliu from "./VerificareDetaliu";
import DeciziiProiect from "./DeciziiProiect";
import VedereStalpCotata, { fundatiaStalpului } from "./DesenStalpCotat";
import CostStivaCard from "./CostStivaCard";
import ReazemArticulat, { reazemulArticulat } from "./ReazemArticulat";
import NotaElement from "./NotaElement";
import CardFundatieAx from "./CardFundatieAx";
import { VedereLamelaCotata } from "./SectiuneLamela";
import { S } from "./Simbol";
import PlanArmareElement from "./PlanArmareElement";
import {
  ElementIndexat,
  TIP_ETICHETA,
  VERDICT_CHIP,
  gasesteElement,
  indexElemente,
  nr,
} from "./elemente";
import { hashElement, hashSectiune, nivelDinRef, pasScurt, refFaraNivel } from "./legaturi";

// bara „cât de departe de 1,00": R/E sub 1 = roșu, sub ~1,1 = chihlimbar.
// Reperul negru stă la 1,00 (74 % din lățime, ca în planșă — capătul barei
// e ~1,35, adică o rezervă de 35 % umple tot).
//
// `ton` suprascrie culoarea derivată din R/E, pentru verificările la care
// raportul NU decide verdictul: cutia rigidă are raportul 47 și tot pică
// (dintr-o condiție booleană), iar o bară verde lângă un ✗ e exact
// contradicția pe care o vânăm. Fără `ton`, culoarea rămâne cea din R/E.
export function BaraRE({
  re,
  latime = 64,
  ton,
}: {
  re: number | null;
  latime?: number;
  ton?: "bad" | "amb" | "ok" | "mut" | "out";
}) {
  if (re == null || !isFinite(re)) return <span className="sg-mono">—</span>;
  const culoare = ton
    ? `var(--${ton})`
    : re < 1 ? "var(--bad)" : re < 1.1 ? "var(--amb)" : "var(--ok)";
  const pct = Math.max(4, Math.min(100, (re / 1.35) * 74));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: latime, height: 5, borderRadius: 99, background: "#e5e5e5",
          position: "relative", display: "inline-block",
        }}
      >
        <span style={{
          position: "absolute", left: 0, top: 0, height: "100%",
          width: `${pct}%`, borderRadius: 99, background: culoare,
        }} />
        <span style={{
          position: "absolute", left: "74%", top: -3, width: 1.5, height: 11,
          background: "var(--ink)",
        }} />
      </span>
      <b className="sg-mono" style={{ fontSize: 12, color: culoare }}>{nr(re, 2)}</b>
    </span>
  );
}

function fmtVal(v: number | string | null | undefined): string {
  if (v == null) return "—";
  if (typeof v === "number") return nr(v, Math.abs(v) < 10 ? 2 : 1);
  return String(v);
}

export default function ElementPage({
  id,
  cloneId,
  refElement,
  solutie,
  steps,
}: {
  id: string;
  // clona din care se CITESC datele (soluția vizualizată; null = proiectul):
  // extrasul de armătură al elementului se cere de la ea, ca să fie al
  // aceleiași geometrii cu verificările
  cloneId?: string | null;
  refElement: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
}) {
  const idx = useMemo(() => indexElemente(steps), [steps]);
  // rândul de verificare DESCHIS (detaliul inline) + catalogul explicațiilor
  const [deschis, setDeschis] = useState<number | null>(null);
  const catalog = useCatalogVerificari();
  const el = useMemo(() => gasesteElement(idx, refElement), [idx, refElement]);

  // AXUL grinzii: celelalte deschideri coliniare, în ordinea de pe ax. Pasul
  // 13 pune pe fiecare grindă `ax.id`; aici se strâng la loc, ca planșa să
  // poată desena diagrama pe tot axul, nu doar pe deschiderea din pagină.
  const axGrinzi = useMemo(() => {
    const idAx = el?.tip === "grinda" ? el.brut?.ax?.id : null;
    if (!idAx) return undefined;
    const lst = idx
      .filter((z) => z.tip === "grinda" && z.brut?.ax?.id === idAx)
      .map((z) => ({ g: z.brut as GrindaArmata, verificari: z.verificari }))
      .sort((a, b) => (a.g.ax?.indice ?? 0) - (b.g.ax?.indice ?? 0));
    return lst.length > 1 ? lst : undefined;
  }, [idx, el]);
  const axGrinziBrut = useMemo(
    () => axGrinzi?.map((z) => z.g), [axGrinzi]);
  // croiala barelor axului de FUNDARE (Pasul 18)
  const croireAxFundare = useMemo(() => {
    if (el?.tip !== "ax") return null;
    const rez = steps["armare_fundatie"]?.result as
      { croire?: CroireAxFundare[] } | null | undefined;
    const nume = (el.brut as { ax?: string } | undefined)?.ax;
    return (rez?.croire ?? []).find((c) => c.ax === nume) ?? null;
  }, [steps, el]);
  // croiala barelor axului (Pasul 13): din ea se desenează suprapunerile
  const croireGrinda = useMemo(
    () => croireAxului(
      steps["armare_grinzi"]?.result as ArmareResult | null | undefined,
      el?.tip === "grinda" ? (el.brut as GrindaArmata | undefined) : null),
    [steps, el]);
  const fem = steps["model_fem"]?.result as FemResult | null | undefined;
  // PILA și GAZDA ei: cifrele reacțiunii stau pe grinda-gazdă (Pasul 13,
  // `transfer`), deci pe pagina pilei se caută grinda, pe pagina gazdei e
  // elementul însuși. `null` = element obișnuit sau Pasul 13 nerulat.
  const reazemTransfer = useMemo(() => {
    const rol = reazemulArticulat(el?.ref ?? "", fem?.model);
    if (!rol) return null;
    const refGazda = rol.rol === "pila" ? rol.gazda : rol.grinda;
    const g = idx.find((z) => z.tip === "grinda" && z.ref === refGazda)?.brut as
      GrindaArmata | undefined;
    return g?.transfer ?? null;
  }, [el, fem, idx]);
  // La stâlpi: se desenează NIVELUL elementului (implicit) sau stiva întreagă,
  // cross-etaje — și atunci apare și costul stâlpului întreg
  //.
  const [totStalpul, setTotStalpul] = useState(false);
  // scopul VEDERII 3D — propriu, nu cel al vederii cotate: sunt două planșe
  // diferite, iar omul le vrea la scopuri diferite.
  // Implicit: piesa din pagină, nu întregul din care face parte.
  const [tot3D, setTot3D] = useState(false);
  // vederea 3D pe TOATĂ lățimea paginii: la un ax de patru deschideri, 380 px
  // de coloană nu ajung ca să se vadă barele. Verificările coboară sub ea.
  const [lat3D, setLat3D] = useState(false);
  const fundatie = useMemo(
    () => fundatiaStalpului(steps, refElement),
    [steps, refElement]
  );
  // geometria ochiului de placă (contur, laturi, goluri) stă în modelul
  // Pasului 7, nu în rezultatul Pasului 16 — desenul 3D o cere de acolo
  const panouGeo = useMemo(
    () =>
      el?.tip === "ochi"
        ? (fem?.model?.panouri ?? []).find((p) => p.id === el.ref) ?? null
        : null,
    [el, fem]
  );

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // istoricul op-urilor P17 pe elementul ăsta (raportul ultimei iterații)
  const modificari = useMemo(() => {
    const r = steps["optimizare_structura"]?.result as any;
    const randuri = (r?.raport_solutie?.modificari ?? []) as any[];
    const stem = refFaraNivel(refElement);
    return randuri.filter(
      (m) => m.element && refFaraNivel(String(m.element)) === stem
    );
  }, [steps, refElement]);
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const t = useT();

  if (!el) {
    return (
      <div className="ob-pagina ob-pagina-ingusta">
        <div className="ob-pipe-head"><h2>{refElement}</h2></div>
        <div className="sg-muted">
          {t("struct.elp.negasit")}{" "}
          <a href={hashSectiune(id, "elemente", solutie ?? null)}>
            {t("struct.elp.inapoi")}
          </a>
        </div>
      </div>
    );
  }

  const chip = VERDICT_CHIP[el.verdict];
  const nrPe = (v: ElementIndexat["verdict"]) =>
    el.verificari.filter((x) => {
      const n = x.verdict.startsWith("SE VERIFICA")
        ? "conform"
        : x.verdict.startsWith("ATENTIE")
          ? "la_limita"
          : "neconform";
      return n === v;
    }).length;
  // următorul element care pică (după sortarea listei) — drumul de lucru
  const urmatoarea = idx
    .filter((e) => e.verdict === "neconform" && e.ref !== el.ref)
    .sort((a, b) => (a.reMin ?? Infinity) - (b.reMin ?? Infinity))[0];

  const linkPas = hashSectiune(id, el.pas, solutie ?? null, {
    ref: el.ref,
    nivel: nivelDinRef(el.ref),
  });
  // elementul poartă chiar valorile E/R (grinzi, pereți, ochiuri, axe) sau
  // doar R/E + `detaliu` (stâlpi)?
  const areEV = el.verificari.some((v) => v.E != null || v.R != null);
  const esteStalp = el.tip === "stalp";
  // grinzile au elevație și stații: rândul poartă poziția punctului și bara
  // efort/rezistență (direcțiile A + C din canvasul de design)
  const esteGrinda = el.tip === "grinda" && !!el.brut?.puncte;
  const stem = refFaraNivel(el.ref);

  // VEDEREA 3D, ca funcție: stă fie în coloana din dreapta, fie — extinsă cu
  // butonul din colț — pe toată lățimea paginii, cu verificările sub ea.
  const cardul3D = () => areVedere3D(el, panouGeo) && (
            <div className="ob-card" style={{ padding: 10, overflow: "hidden" }}>
              {/* titlul și comutatorul de SCOP stau pe același rând: „ce piesă
                  văd" e o proprietate a planșei, nu un reglaj de dedesubt
                   */}
              <div style={{ display: "flex", alignItems: "center", gap: 8,
                            flexWrap: "wrap", paddingBottom: 6 }}>
                <span className="ob-lbl">
                  {t("struct.elp.titlu_3d", {
                    tip: t(TIP_ETICHETA[el.tip]), ref: el.ref,
                  })}
                </span>
                <span style={{ flex: 1 }} />
                <button className="ob-expand" onClick={() => setLat3D(!lat3D)}
                        title={t(lat3D
                          ? "struct.elp.restrange_titlu"
                          : "struct.elp.extinde_titlu")}
                        aria-label={t(lat3D
                          ? "struct.elp.restrange" : "struct.elp.extinde")}>
                  <IconExpand strans={!lat3D} />
                </button>
                {(esteStalp || axGrinziBrut) && (
                  <div className="pf-tabs" style={{ marginBottom: 0 }}>
                    <button className={`pf-tab ${!tot3D ? "active" : ""}`}
                            onClick={() => setTot3D(false)}
                            title={t(esteStalp
                              ? "struct.elp.doar_nivel_stalp"
                              : "struct.elp.doar_deschidere")}>
                      {t(esteStalp ? "struct.elp.doar_stalpul" : "struct.elp.doar_grinda",
                         { ref: el.ref })}
                    </button>
                    <button className={`pf-tab ${tot3D ? "active" : ""}`}
                            onClick={() => setTot3D(true)}
                            title={esteStalp
                              ? t("struct.elp.stiva_titlu")
                              : t("struct.elp.ax_titlu", { n: el.brut?.ax?.n_grinzi })}>
                      {esteStalp
                        ? t("struct.elp.tot_stalpul", { ref: stem })
                        : t("struct.elp.tot_axul", { n: el.brut?.ax?.n_grinzi })}
                    </button>
                  </div>
                )}
              </div>
              <Vedere3DElement
                el={el}
                model={fem?.model}
                panou={panouGeo}
                totStalpul={tot3D}
                totAxul={tot3D}
                axGrinzi={axGrinziBrut}
                croire={croireGrinda}
                fundatie={fundatie}
              />
            </div>
  );

  return (
    <div className="ob-pagina" style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div className="ob-pas-antet" style={{ paddingTop: 0 }}>
        <a href={hashSectiune(id, "acasa", solutie ?? null)}>
          {t("struct.vp.disciplina")}
        </a>
        <span>/</span>
        <a href={hashSectiune(id, "elemente", solutie ?? null)}>
          {t("struct.elp.capitolul5")}
        </a>
        <span>/</span>
        <span>{t(TIP_ETICHETA[el.tip])}</span>
        <span>/</span>
        <b style={{ color: "var(--ink)" }}>{el.ref}</b>
        {urmatoarea && (
          <span className="ob-pas-nav">
            <a href={hashElement(id, urmatoarea.ref, solutie)}
               title={t("struct.elp.urmatoarea_titlu")}>
              {t("struct.elp.urmatoarea", { ref: urmatoarea.ref })}
            </a>
          </span>
        )}
      </div>

      {/* cartea de identitate */}
      <div className="ob-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <b className="ob-disp" style={{ fontSize: 22 }}>{el.ref}</b>
            <span className="ob-chip ob-c-mut">{t(TIP_ETICHETA[el.tip])} · {el.nivel}</span>
            <span className={`ob-chip ${chip.cls}`}>{t(chip.text)}</span>
          </div>
          <span style={{ color: "var(--mut)", fontSize: 12 }}>
            {el.sectiune}
            {el.detaliu ? ` · ${el.detaliu}` : ""}
          </span>
        </div>
        <div style={{ width: 1, height: 44, background: "var(--line)" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span className="ob-lbl">{t("struct.elp.verificarile")}</span>
          <div style={{ display: "flex", gap: 4 }}>
            {nrPe("neconform") > 0 && (
              <span className="ob-chip ob-c-bad">
                {t("struct.elp.picate", { n: nrPe("neconform") })}
              </span>
            )}
            {nrPe("la_limita") > 0 && (
              <span className="ob-chip ob-c-amb">
                {t("struct.sumar.la_limita", { n: nrPe("la_limita") })}
              </span>
            )}
            <span className="ob-chip ob-c-ok">
              {t("struct.sumar.trec", { n: nrPe("conform") })}
            </span>
          </div>
        </div>
        {/* VERIFICAREA CARE GUVERNEAZĂ — după verdict, ca titlul să se
            citească «trece / pică», apoi «de ce»: numele verificării cu cea
            mai mică rezervă, la mărime normală, cu bara și raportul ei */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}
             data-testid="elp-guverneaza">
          <span className="ob-lbl">
            {t("struct.elp.re_minim")}{" "}
            <span style={{ fontWeight: 400, textTransform: "none" }}
                  title={t("struct.elp.guverneaza_titlu")}>
              (<S k="sim:R_E">R/E</S>)
            </span>
          </span>
          <span style={{ fontSize: 13 }}>{el.criteriu || "—"}</span>
          <BaraRE re={el.reMin} latime={110} />
        </div>
        <span style={{ flex: 1 }} />
        {(el.otelKg != null || el.betonM3 != null) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, textAlign: "right" }}>
            <span className="ob-lbl">{t("struct.elp.cantitati")}</span>
            <b className="sg-mono" style={{ fontSize: 13 }}>
              {el.otelKg != null
                ? t("struct.inv.kg_otel", { n: nr(el.otelKg) }) : ""}
              {el.otelKg != null && el.betonM3 != null ? " · " : ""}
              {el.betonM3 != null
                ? t("struct.inv.m3_beton", { n: nr(el.betonM3, 2) }) : ""}
            </b>
          </div>
        )}
      </div>

      {/* EXTINSĂ: planșa ia toată lățimea, iar verificările coboară sub ea */}
      {lat3D && (
        <div style={{ paddingBottom: 12 }}>{cardul3D()}</div>
      )}

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {/* STÂNGA: verificările, în formatul simplificat — un rând pe
            verificare, cu valoarea, pragul și bara; TOT rândul e un link
            spre pagina expandată (pasul care a emis-o, cu elementul
            selectat), unde stau formulele și explicațiile complete
             */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="ob-card" style={{ overflow: "hidden" }}>
          <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <b>{t("struct.elp.ver_titlu")}</b>
            <span className="ob-acasa-sub">
              {t("struct.elp.ver_sub", { pas: pasScurt(el.pas) })}
            </span>
          </div>
          <table className="ob-sol-tabel ob-ver-lista">
            <thead>
              <tr>
                <th style={{ width: 26 }} />
                <th>{t("struct.elp.col_verificarea")}</th>
                <th style={{ width: esteGrinda ? 132 : 60 }}>
                  {t(esteGrinda
                    ? "struct.elp.col_punct_grinda" : "struct.elp.col_punct")}
                </th>
                {/* verificările stâlpilor nu poartă E/R (au `detaliu`):
                    coloanele goale s-ar umple cu „—", deci apar doar când
                    elementul are chiar valorile */}
                {areEV && (
                  <th style={{ width: 100 }}>{t("struct.elp.col_valoare")}</th>
                )}
                {areEV && (
                  <th style={{ width: 100 }}>{t("struct.elp.col_capabil")}</th>
                )}
                {!areEV && <th>{t("struct.elp.col_sursa")}</th>}
                {/* la grinzi bara arată CÂT din capacitate e consumat
                    (direcția C din canvasul de design); la
                    celelalte elemente rămâne bara „cât de departe de 1,00" */}
                <th style={{ width: 150 }}>
                  {esteGrinda
                    ? t("struct.elp.col_efort") : t("struct.tipver.col_re")}
                </th>
                <th style={{ width: 100 }}>{t("struct.vp.grup_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {[...el.verificari]
                .sort((a, b) => (a.RE ?? Infinity) - (b.RE ?? Infinity))
                .map((v, i) => {
                  const norma = v.verdict.startsWith("SE VERIFICA")
                    ? "conform"
                    : v.verdict.startsWith("ATENTIE")
                      ? "la_limita"
                      : "neconform";
                  const chip = VERDICT_CHIP[norma as keyof typeof VERDICT_CHIP];
                  return (
                    <>
                    <tr
                      key={i}
                      onClick={() => setDeschis(deschis === i ? null : i)}
                      className={deschis === i ? "deschis" : undefined}
                      title={t("struct.elp.rand_titlu")}
                    >
                      <td className="ob-ver-sageata">{deschis === i ? "▾" : "▸"}</td>
                      <td style={{ fontSize: 12 }}>
                        {v.nume}
                        {v.acceptatMarja != null && (
                          <span className="ob-chip ob-c-amb" style={{ fontSize: 10, marginLeft: 6 }}
                                title={t("struct.elp.marja_titlu", { n: v.acceptatMarja })}>
                            {t("struct.elp.marja", { n: v.acceptatMarja })}
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: 11, color: "var(--mut)" }}>
                        {esteGrinda ? (
                          <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <span className="sg-mono" style={{ fontSize: 11 }}>
                              {v.punct ?? "—"}
                            </span>
                            <MiniPozitie g={el.brut} punct={v.punct} nume={v.nume}
                                         culoare={tonVerdict(v.RE, v.verdict)}
                                         latime={74} />
                          </span>
                        ) : (
                          v.punct ?? "—"
                        )}
                      </td>
                      {areEV && (
                        <>
                          <td className="sg-mono" style={{ fontSize: 12 }}>{fmtVal(v.E)}</td>
                          <td className="sg-mono" style={{ fontSize: 12, color: "var(--mut)" }}>
                            {fmtVal(v.R)}
                          </td>
                        </>
                      )}
                      {!areEV && (
                        <td style={{ fontSize: 11, color: "var(--mut)" }}>
                          {v.referinta ?? "—"}
                        </td>
                      )}
                      <td>
                        {(esteGrinda &&
                          typeof v.E === "number" &&
                          typeof v.R === "number" && (
                            <BaraEfortRezistenta E={v.E} R={v.R} RE={v.RE}
                                                 verdict={v.verdict} />
                          )) || <BaraRE re={v.RE} />}
                      </td>
                      <td>
                        <span className={`ob-chip ${chip.cls}`} style={{ fontSize: 10 }}>
                          {t(chip.text)}
                        </span>
                      </td>
                    </tr>
                    {deschis === i && (
                      <tr className="ob-ver-rand-detaliu">
                        <td />
                        <td colSpan={areEV ? 5 : 4}>
                          <VerificareDetaliu
                            v={v}
                            pas={el.pas}
                            catalog={catalog}
                            linkPas={linkPas}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                  );
                })}
            </tbody>
          </table>
          <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--mut)" }}>
            {t("struct.elp.nota_tabel")}
          </div>
        </div>
        {/* deciziile de proiect, separat: nu trec și nu pică */}
        <DeciziiProiect decizii={el.decizii ?? []} />
        {/* planul de armare al elementului, IMEDIAT după verificări: blocul
            lui de pe coală (cu ecran complet); lipsește când elementul n-are
            încă desen */}
        <PlanArmareElement projectId={id} cloneId={cloneId ?? null}
                           refElement={el.ref} solutie={solutie ?? null} />
        {/* SUB verificări, la o PILĂ sau la GAZDA ei: reazemul articulat —
            desenul cu N, V și M = 0, plus cifrele reacțiunii din Pasul 13.
            Pentru orice alt element componenta întoarce null. */}
        <ReazemArticulat refElement={el.ref} model={fem?.model}
                         transfer={reazemTransfer} projectId={id}
                         solutie={solutie ?? null} />
        {/* SUB verificări, la grinzi: DIAGRAMA celor 5 puncte — unde stau
            stațiile R1/câmp/R2 pe elevația grinzii, cu M_Ed/M_Rd și R/E
            fiecăreia. Tabelul de deasupra le numește,
            desenul le arată. */}
        {el.tip === "grinda" && el.brut?.puncte && (
          <div className="ob-card" style={{ padding: "11px 14px" }}>
            <PuncteGrinda g={el.brut} verificari={el.verificari}
                          croire={croireGrinda}
                          ax={axGrinzi} />
          </div>
        )}
        {/* SUB verificări, la AXELE de fundație: planșa înfășurătoarei —
            M⁺/M⁻ pe lungime, A_s cerut vs. efectiv, elevația cu barele tăiate
            și |V| peste zonele de etrieri. Verificările de deasupra poartă
            acum stația („x = 4,25 m"); desenul spune unde e stația aceea
            . */}
        {el.tip === "ax" && el.brut?.armare?.statii && (
          <div className="ob-card" style={{ padding: "11px 14px" }}>
            <span className="ob-lbl" style={{ display: "block", paddingBottom: 6 }}>
              {t("struct.elp.infasuratoare")}
            </span>
            <InfasuratoareAx ax={el.brut.armare as AxArmat} latime={700}
                             croire={croireAxFundare} />
          </div>
        )}
        {/* SUB verificări: inventarul de bare și costurile elementului
            . Secțiunea cotată a URCAT în coloana din
            dreapta, lângă vederea 3D a piesei. */}
        {/* La stâlpi, înaintea secțiunii: VEDEREA COTATĂ — placa, banda
            grinzii, înălțimea liberă, înnădirea, fundația reală. Comutatorul
            alege între nivelul ăsta și stiva întreagă. */}
        {esteStalp && el.brutParinte?.nivele && (
          <div className="ob-card" style={{ padding: "11px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8, flexWrap: "wrap" }}>
              <span className="ob-lbl">
                {t("struct.elp.vedere_cotata", {
                  ce: totStalpul
                    ? t("struct.elp.toate_nivelurile", { stem })
                    : el.ref,
                })}
              </span>
              <span style={{ flex: 1 }} />
              <div className="pf-tabs" style={{ marginBottom: 0 }}>
                <button className={`pf-tab ${!totStalpul ? "active" : ""}`}
                        onClick={() => setTotStalpul(false)}
                        title={t("struct.elp.doar_nivelul")}>
                  {t("struct.elp.doar", { ref: el.ref })}
                </button>
                <button className={`pf-tab ${totStalpul ? "active" : ""}`}
                        onClick={() => setTotStalpul(true)}
                        title={t("struct.elp.stiva_titlu")}>
                  {t("struct.elp.tot_stalpul", { ref: stem })}
                </button>
              </div>
            </div>
            <VedereStalpCotata
              toate={el.brutParinte.nivele}
              selectate={totStalpul ? el.brutParinte.nivele : [el.brut]}
              fundatie={fundatie}
              cuFundatie={totStalpul}
              cCm={Number(el.param?.c_stalp_cm ?? el.param?.acoperire_cm ?? 4) || 4}
              latime={560}
              inaltimeMax={totStalpul ? 520 : 420}
            />
          </div>
        )}
        {/* La lamele: ELEVAȚIA STIVEI — nivelurile ei cu zonele A/B, l_cr de
            la bază, carcasele de capăt și barele inimii, nivelul paginii
            evidențiat */}
        {el.tip === "lamela" && el.brutParinte?.nivele?.length > 0 && (
          <div className="ob-card" style={{ padding: "11px 14px" }}>
            <span className="ob-lbl" style={{ display: "block", paddingBottom: 8 }}>
              {t("struct.elp.vedere_cotata", {
                ce: t("struct.elp.toate_nivelurile", { stem }),
              })}
            </span>
            <VedereLamelaCotata
              nivele={el.brutParinte.nivele}
              selectat={el.ref}
              stiva={el.brutParinte.stiva}
              cCm={Number(el.param?.c_cm ?? el.param?.acoperire_cm ?? 4) || 4}
              latime={560}
            />
          </div>
        )}
        {/* costul stâlpului ÎNTREG apare peste inventar când e cerută stiva —
            inventarul de bare rămâne, e al nivelului și tot util */}
        {esteStalp && totStalpul && el.brutParinte?.nivele && (
          <CostStivaCard projectId={id} cloneId={cloneId ?? null} stiva={el.brutParinte} />
        )}
        <InventarElement projectId={id} cloneId={cloneId ?? null} el={el}
                         solutie={solutie ?? null} />
        </div>

        {/* DREAPTA: două view-uri — VEDEREA 3D a elementului, apoi contextul
            lui în clădire; dedesubt istoricul op-urilor Pasului 19 */}
        <div style={{ width: 380, flex: "none", display: "flex", flexDirection: "column", gap: 12 }}>
          {!lat3D && cardul3D()}
          {/* secțiunea cotată stă lângă vederea 3D, nu sub verificări: e
              aceeași piesă privită altfel, iar coloana din stânga rămâne
              coloana CIFRELOR */}
          <div className="ob-card" style={{ padding: "11px 14px" }}>
            <span className="ob-lbl" style={{ display: "block", paddingBottom: 8 }}>
              {t("struct.elp.sectiunea")}
            </span>
            <DesenElement el={el} />
          </div>
          {fem?.model && (
            <div className="ob-card" style={{ padding: 8 }}>
              <span className="ob-lbl" style={{ display: "block", padding: "2px 4px 6px" }}>
                {t("struct.elp.contextul")}
              </span>
              <LocalizareCladire
                model={fem.model}
                evidentiate={[el.ref, refFaraNivel(el.ref)]}
                height={230}
                projectId={id}
                solutie={solutie ?? null}
                cuPanouri={el.tip === "ochi"}
              />
            </div>
          )}
          {(modificari.length > 0 || (el.istoric?.length ?? 0) > 0) && (
            <div className="ob-card" style={{ padding: "11px 13px", display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="ob-lbl">{t("struct.elp.istoric")}</span>
              {modificari.map((m, i) => (
                <div key={i} style={{ fontSize: 11 }}>
                  <span className={`ob-chip ${
                    m.stare === "aplicata" ? "ob-c-ok"
                    : m.stare === "refuzata" ? "ob-c-bad" : "ob-c-amb"
                  }`} style={{ fontSize: 10 }}>{m.stare ?? "?"}</span>{" "}
                  <span style={{ color: "var(--mut)" }}>
                    {m.modificare ?? m.verificare ?? m.cheie}
                  </span>
                </div>
              ))}
              {el.istoric?.map((h, i) => (
                <div key={`h${i}`} style={{ fontSize: 11, color: "var(--mut)" }}>
                  {t("struct.elp.istoric_rand", {
                    de_ce: h.de_ce, de_la: h.de_la, la: h.la,
                  })}
                  <span className="sg-mono">
                    {t("struct.elp.istoric_re", { n: nr(h.re, 2) })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* la AXELE de fundație, înaintea notei: FUNDAȚIA Pasului 17 — talpa
          axului, combinațiile lui una câte una și desenele care explică
          citirea presiunilor; nota de dedesubt e a armării (Pasul 18) */}
      {el.tip === "ax" && (
        <CardFundatieAx ax={el.ref} steps={steps} id={id} solutie={solutie ?? null} />
      )}
      {/* NOTA DE CALCUL COMPLETĂ, pe toată lățimea: ce arăta pagina pasului,
          pe elementul ăsta — ca pagina să fie singurul loc unde se citește
          cum se cofrează și cum se armează */}
      <NotaElement el={el} steps={steps} id={id} solutie={solutie ?? null} />
    </div>
  );
}

/** Colțurile de extindere — patru paranteze, nimic în mijloc: semnul standard
 *  pentru «lățește planșa». Săgețile se întorc spre interior când e extinsă. */
function IconExpand({ strans }: { strans: boolean }) {
  const d = strans
    ? "M1,5 L1,1 L5,1 M11,1 L15,1 L15,5 M15,11 L15,15 L11,15 M5,15 L1,15 L1,11"
    : "M5,1 L5,5 L1,5 M11,5 L15,5 M11,5 L11,1 M11,11 L15,11 M11,11 L11,15 M5,15 L5,11 L1,11";
  return (
    <svg viewBox="0 0 16 16" width={14} height={14} aria-hidden>
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.6}
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
